import type { Nullish } from '@iheartjane/types';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { CartActionResult } from '@jane/bloom/actions/cart.server';
import { BrandSpecialShortfallModal } from '@jane/bloom/components/brandSpecialShortfallModal';

const BrandSpecialShortfallContext = createContext<
  (result: Nullish<CartActionResult>) => void
>(() => undefined);

/**
 * The API reports a dropped Jane Gold reward only on the cart request that drops it, so
 * every fetcher that calls a cart action reports its result here.
 */
export const useBrandSpecialShortfallNotice = (
  fetcherData: Nullish<CartActionResult>
) => {
  const report = useContext(BrandSpecialShortfallContext);

  useEffect(() => report(fetcherData), [fetcherData, report]);
};

export const BrandSpecialShortfallProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // Several components can watch one keyed fetcher; a late subscriber must not reopen the modal.
  const shownResults = useRef(new WeakSet<object>());

  const report = useCallback((result: Nullish<CartActionResult>) => {
    const warning = result?.payload?.warning;
    if (!warning || shownResults.current.has(result)) return;

    shownResults.current.add(result);
    setOpen(true);
  }, []);

  return (
    <BrandSpecialShortfallContext.Provider value={report}>
      {children}
      <BrandSpecialShortfallModal
        open={open}
        onRequestClose={() => setOpen(false)}
      />
    </BrandSpecialShortfallContext.Provider>
  );
};
